// frontend/src/pages/PayrollDone/components/EmployeeTable.tsx
// "Paid this run" table: one row per employee with name, payment method, net pay,
// a "view pay stub" icon button, and an inline cheque-number input (when applicable).

import React from "react";
import { Check, FileSearch } from "lucide-react";
import type { DoneViewEmployee } from "../types";
import { fmtMoney } from "../helpers";
import { C, FONT, tabular, chequeInput } from "../styles";

interface EmployeeTableProps {
  employees: DoneViewEmployee[];
  chequeNumbers: Record<string, string>;
  onChequeNumberChange: (stubId: string, value: string) => void;
  onOpenPayStub: (stubId: string) => void;
}

const GRID_COLUMNS = "40px 1.4fr 160px 160px 200px";

export default function EmployeeTable({
  employees,
  chequeNumbers,
  onChequeNumberChange,
  onOpenPayStub,
}: EmployeeTableProps): React.ReactElement {
  return (
    <div style={{ marginBottom: 40 }}>
      <div
        style={{
          fontSize: 20,
          fontWeight: 700,
          color: C.ink,
          marginBottom: 20,
          paddingBottom: 12,
          borderBottom: "1.5px solid " + C.ink,
        }}
      >
        Paid this run ({employees.length})
      </div>

      {employees.length === 0 ? (
        <div style={{ padding: "24px 0", fontSize: 14, color: C.ink, fontWeight: 500 }}>
          No employees in this pay run.
        </div>
      ) : (
        <>
          {/* Header row */}
          <div
            style={{
              padding: "12px 0",
              display: "grid",
              gridTemplateColumns: GRID_COLUMNS,
              gap: 12,
              fontSize: 10,
              fontWeight: 700,
              color: C.ink,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              borderBottom: "1px solid " + C.line,
            }}
          >
            <div></div>
            <div>Employee name</div>
            <div>Payment method</div>
            <div style={{ textAlign: "right" }}>Net pay</div>
            <div>Cheque number</div>
          </div>

          {/* Body rows */}
          {employees.map((emp) => (
            <div
              key={emp.stub_id}
              style={{
                padding: "16px 0",
                display: "grid",
                gridTemplateColumns: GRID_COLUMNS,
                gap: 12,
                alignItems: "center",
                borderBottom: "1px solid " + C.line,
                fontSize: 14,
                color: C.ink,
              }}
            >
              {/* Green checkmark */}
              <div>
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 5,
                    background: C.brand,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Check size={12} strokeWidth={3.5} stroke={C.card} />
                </div>
              </div>

              {/* Name + position */}
              <div>
                <div style={{ fontWeight: 700 }}>{emp.name}</div>
                {emp.position_title && (
                  <div style={{ fontSize: 12, color: C.ink, fontWeight: 500, marginTop: 2 }}>
                    {emp.position_title}
                  </div>
                )}
              </div>

              {/* Payment method pill */}
              <div>
                <span
                  style={{
                    fontSize: 10.5,
                    color: C.ink,
                    fontWeight: 700,
                    padding: "3px 8px",
                    background: C.page,
                    borderRadius: 5,
                    textTransform: "uppercase",
                  }}
                >
                  {emp.payment_method || "Cheque"}
                </span>
              </div>

              {/* Net pay + view-stub icon */}
              <div
                style={{
                  textAlign: "right",
                  fontWeight: 700,
                  fontSize: 15,
                  ...tabular,
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                {fmtMoney(emp.net_pay)}
                <button
                  onClick={() => onOpenPayStub(emp.stub_id)}
                  title="View pay stub"
                  aria-label="View pay stub"
                  style={{
                    background: "transparent",
                    border: "none",
                    padding: 4,
                    borderRadius: 6,
                    cursor: "pointer",
                    color: "#66748B",
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                >
                  <FileSearch size={16} strokeWidth={2} />
                </button>
              </div>

              {/* Cheque number input (only for cheque payees) */}
              <div>
                {emp.is_cheque && (
                  <input
                    value={chequeNumbers[emp.stub_id] || ""}
                    onChange={(e) => onChequeNumberChange(emp.stub_id, e.target.value)}
                    placeholder="Enter cheque number"
                    style={{ ...chequeInput, fontFamily: FONT }}
                  />
                )}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}